import { useMemo, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, Check } from "lucide-react";
import cities from "../cities_minified.json";
import { trackEvent } from "../utils/metaPixel";
import { saveUserSession, getSafeNext } from "../utils/session";

const API = "https://bhavanaastro.onrender.com/api";
const DEFAULT_NEXT = "/ai-consultancy"; // new users want the chat, not the dashboard

const authCss = `
  .as-auth { font-family: 'Hind', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; }
  .as-auth-display { font-family: 'Baloo 2', 'Hind', system-ui, sans-serif; }
`;

const inputCls =
  "w-full rounded-xl border border-[#DAD6F0] bg-white px-4 py-3 text-[16px] text-[#17143F] placeholder:text-slate-400 focus:border-[#3A2E9C] focus:outline-none focus:ring-2 focus:ring-[#3A2E9C]/20";
const labelCls = "mb-1.5 block text-sm font-semibold text-[#17143F]";
const primaryBtn =
  "as-auth-display flex w-full items-center justify-center rounded-2xl bg-[#FFB627] px-6 py-3.5 text-[18px] font-bold text-[#1E1A4D] shadow-[0_10px_28px_-10px_rgba(255,182,39,0.7)] transition hover:bg-[#FFC24D] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60";

export default function UserRegister() {
  const navigate = useNavigate();
  const location = useLocation();
  const next = getSafeNext(location.search) || DEFAULT_NEXT;

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    dob: "",
    birthTime: "",
    birthPlace: "",
    city: "",
    lat: null,
    lon: null,
    mobile: "",
  });
  const [unknownTime, setUnknownTime] = useState(false);
  const [cityQuery, setCityQuery] = useState("");
  const [cityOpen, setCityOpen] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const today = new Date().toISOString().split("T")[0];

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  // ----- birth place: search instead of a 500-city dropdown -----
  const cityMatches = useMemo(() => {
    const q = cityQuery.trim().toLowerCase();
    if (q.length < 2 || form.lat) return [];
    const label = (c) => `${c.city}, ${c.state}`.toLowerCase();
    return cities
      .filter((c) => label(c).includes(q))
      .sort((a, b) => Number(!a.city.toLowerCase().startsWith(q)) - Number(!b.city.toLowerCase().startsWith(q)))
      .slice(0, 6);
  }, [cityQuery, form.lat]);

  const onCityInput = (e) => {
    setCityQuery(e.target.value);
    setCityOpen(true);
    // typing again clears the previous pick
    setForm((f) => ({ ...f, birthPlace: "", city: "", lat: null, lon: null }));
  };

  const pickCity = (c) => {
    setCityQuery(`${c.city}, ${c.state}`);
    setCityOpen(false);
    setForm((f) => ({
      ...f,
      birthPlace: c.city,
      city: c.city,
      lat: c.latitude,
      lon: c.longitude,
    }));
  };

  const onUnknownTime = (checked) => {
    setUnknownTime(checked);
    setForm((f) => ({ ...f, birthTime: checked ? "12:00" : "" }));
  };

  // ----- steps -----
  const goStep2 = (e) => {
    e.preventDefault();
    setError("");
    setStep(2);
    window.scrollTo({ top: 0 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.lat) {
      setError("List mein se apna birth place chuno.");
      return;
    }
    setError("");
    setLoading(true);

    const payload = {
      ...form,
      name: form.name.trim(),
      email: form.email.trim(),
      mobile: form.mobile.trim(),
    };

    try {
      await axios.post(`${API}/users/register`, payload);

      // Meta Pixel: CompleteRegistration
      trackEvent("CompleteRegistration", {
        content_name: "User Registration",
        status: true,
      });

      // Log the new user in right away, so they land in the chat (no login step)
      try {
        const { data } = await axios.post(`${API}/auth/login`, {
          email: payload.email,
          password: form.password,
        });
        saveUserSession(data);
        navigate(next, { replace: true });
      } catch (loginErr) {
        // Account is created; just ask them to log in, and still carry them to the chat
        navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true });
      }
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Kuch gadbad ho gayi. Dobara try karo.";
      setError(msg); // the form keeps everything they typed
    } finally {
      setLoading(false);
    }
  };

  const loginLink = `/login?next=${encodeURIComponent(next)}`;

  return (
    <div className="as-auth min-h-screen bg-[#F5F4FB] px-4 py-6 sm:py-10">
      <style>{authCss}</style>

      <div className="mx-auto w-full max-w-md">
        <h1 className="as-auth-display text-[29px] font-extrabold leading-tight text-[#17143F]">
          Free chat shuru karo
        </h1>
        <p className="mt-1 text-[15px] leading-snug text-slate-600">
          AI astrologer aapki janam details se kundli dekhta hai. Pehla 1 minute free.
        </p>

        {/* progress */}
        <div className="mt-5 flex items-center gap-2" aria-hidden="true">
          <span className="h-1.5 flex-1 rounded-full bg-[#3A2E9C]" />
          <span className={`h-1.5 flex-1 rounded-full ${step === 2 ? "bg-[#3A2E9C]" : "bg-[#DAD6F0]"}`} />
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Step {step} of 2: {step === 1 ? "Aapki details" : "Janam details"}
        </p>

        <div className="mt-4 rounded-2xl bg-white p-5 shadow-[0_1px_2px_rgba(23,20,63,0.06),0_10px_24px_-14px_rgba(23,20,63,0.25)]">
          {/* ---------- Step 1: account ---------- */}
          {step === 1 && (
            <form onSubmit={goStep2} className="space-y-4">
              <div>
                <label className={labelCls} htmlFor="name">Full name</label>
                <input
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Aapka naam"
                  autoComplete="name"
                  className={inputCls}
                  required
                />
              </div>

              <div>
                <label className={labelCls} htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="aap@example.com"
                  autoComplete="email"
                  className={inputCls}
                  required
                />
              </div>

              <div>
                <label className={labelCls} htmlFor="mobile">Mobile number</label>
                <input
                  id="mobile"
                  type="tel"
                  inputMode="tel"
                  name="mobile"
                  value={form.mobile}
                  onChange={handleChange}
                  placeholder="10 digit mobile number"
                  autoComplete="tel"
                  className={inputCls}
                  required
                />
              </div>

              <div>
                <label className={labelCls} htmlFor="password">Password</label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPw ? "text" : "password"}
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder="Ek password banao"
                    autoComplete="new-password"
                    className={`${inputCls} pr-12`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    aria-label={showPw ? "Password chhupao" : "Password dikhao"}
                    className="absolute inset-y-0 right-0 grid w-12 place-items-center text-slate-500"
                  >
                    {showPw ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <button type="submit" className={primaryBtn}>
                Aage badho
              </button>
            </form>
          )}

          {/* ---------- Step 2: birth details ---------- */}
          {step === 2 && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={labelCls} htmlFor="dob">Date of birth</label>
                <input
                  id="dob"
                  type="date"
                  name="dob"
                  value={form.dob}
                  onChange={handleChange}
                  min="1920-01-01"
                  max={today}
                  className={inputCls}
                  required
                />
              </div>

              <div>
                <label className={labelCls} htmlFor="birthTime">Birth time</label>
                <input
                  id="birthTime"
                  type="time"
                  name="birthTime"
                  value={unknownTime ? "" : form.birthTime}
                  onChange={handleChange}
                  disabled={unknownTime}
                  className={`${inputCls} disabled:bg-slate-50 disabled:text-slate-400`}
                  required={!unknownTime}
                />
                <label className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={unknownTime}
                    onChange={(e) => onUnknownTime(e.target.checked)}
                    className="h-4 w-4 rounded border-[#DAD6F0] accent-[#3A2E9C]"
                  />
                  Mujhe birth time nahi pata
                </label>
                {unknownTime && (
                  <p className="mt-1 text-xs text-slate-500">
                    Hum dopahar 12:00 baje ka approximate time lenge. Isse kundli thodi alag ho sakti hai.
                  </p>
                )}
              </div>

              <div className="relative">
                <label className={labelCls} htmlFor="birthPlace">Birth place</label>
                <div className="relative">
                  <input
                    id="birthPlace"
                    value={cityQuery}
                    onChange={onCityInput}
                    onFocus={() => setCityOpen(true)}
                    onBlur={() => setCityOpen(false)}
                    placeholder="Sheher ka naam likho, jaise Pune"
                    autoComplete="off"
                    className={`${inputCls} ${form.lat ? "pr-11" : ""}`}
                    required
                  />
                  {form.lat && (
                    <span className="absolute inset-y-0 right-3 grid place-items-center text-green-600">
                      <Check size={20} />
                    </span>
                  )}
                </div>

                {cityOpen && cityMatches.length > 0 && (
                  <ul className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-[#DAD6F0] bg-white shadow-lg">
                    {cityMatches.map((c) => (
                      <li key={`${c.city}-${c.state}-${c.district}`}>
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            pickCity(c);
                          }}
                          className="block w-full px-4 py-3 text-left text-[15px] text-[#17143F] hover:bg-[#F5F4FB]"
                        >
                          {c.city}, <span className="text-slate-500">{c.state}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {cityQuery.trim().length >= 2 && !form.lat && cityMatches.length === 0 && (
                  <p className="mt-1 text-xs text-slate-500">
                    Sheher nahi mila. Spelling check karo ya paas ka bada sheher chuno.
                  </p>
                )}
              </div>

              {error && (
                <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
                  {error}
                </p>
              )}

              <button type="submit" disabled={loading} className={primaryBtn}>
                {loading ? "Chat khul rahi hai..." : "Chat shuru karo"}
              </button>

              <button
                type="button"
                onClick={() => {
                  setError("");
                  setStep(1);
                }}
                className="block w-full text-center text-sm font-medium text-[#3A2E9C]"
              >
                Peeche jao
              </button>
            </form>
          )}
        </div>

        <p className="mt-4 text-center text-sm text-slate-600">
          Pehle se account hai?{" "}
          <Link to={loginLink} className="font-semibold text-[#3A2E9C] underline underline-offset-2">
            Login karo
          </Link>
        </p>
        <p className="mt-2 text-center text-xs text-slate-500">
          Aapki details private aur confidential rehti hain.
        </p>
      </div>
    </div>
  );
}